/**
 * DanismanCockpitShared.jsx
 *
 * `AdminDanismanCockpit.jsx` (Danışman Paneli, /admin/cockpit — Ödemesi
 * Gelenler + Satış Taleplerim + Hatırlatmalar + Datalar bloklarının hepsini
 * barındırır, bkz. components/crm/DatalarBlock.jsx) tarafından kullanılan
 * paylaşılan sekme içerikleri + yardımcı bileşenler.
 *
 * Faz 2/3 yeniden düzenlemesi (bkz. proje-yoneticisi görev talimatı,
 * 2026-08-13): eskiden AdminDanismanCockpit.jsx içinde tek dosyada duran
 * StatusBadge / OdemeModal / Son Aramalar / Hatırlatmalar / Data Detay
 * Geçmiş / Görüşme Sheet bileşenleri buraya taşındı — davranış DEĞİŞMEDİ
 * (Potansiyeller sekmesi hariç; o DatalarBlock.jsx içinde 7.png/8.png
 * sözleşmesine göre YENİDEN yazıldı, burada değil). GunSonuModal (Gün Sonu
 * Raporu) Faz 4'te SİLİNDİ — hiçbir sayfadan çağrılmıyordu, Gün Sonu Raporu
 * artık AdminDanismanAnaSayfa.jsx Blok 3b'de kalıcı panel olarak yaşıyor.
 *
 * Faz 4 (bkz. proje-yoneticisi görev talimatı, 2026-08-13 — 9.png referansı):
 * eskiden yandan açılan `GorusmeSheet` + `LeadDetailSheet` (burada) ve
 * `AdminAssignedLeads.jsx` içindeki üçüncü bir kopya `GorusmePaneli`, TEK bir
 * ortada-modal bileşen olan `LeadDetayModal`'a indirildi. Davranış farkları
 * (admin editable / danisman salt okunur alan bloğu) modal İÇİNDE `isAdmin`
 * prop'una göre dallanır.
 *
 * Faz 5 (bkz. proje-yoneticisi görev talimatı, 2026-08-13): eskiden ayrı bir
 * sayfa olan `AdminDanismanDatalar.jsx` (/danisman/datalar) kaldırıldı —
 * içeriği `components/crm/DatalarBlock.jsx`'e taşındı ve Danışman Paneli'nin
 * altında render ediliyor. Eski adres /admin/cockpit'e redirect edilir.
 *
 * Faz 6 (bkz. proje-yoneticisi görev talimatı, 2026-08-13 — Atanan Leadler
 * kaldırma turu): `AdminAssignedLeads.jsx` (/danisman/leads) sayfası
 * kaldırıldı. İçindeki 7 işlev şuraya taşındı: Dönüştür / Satışa Hazır
 * İşaretle / Kimlik Oluştur / Profil eylemleri `LeadDetayModal`'ın altındaki
 * "İşlemler" bölümüne (bkz. dosya içi ilgili yorum); "Satış Taleplerim"
 * katlanabilir listesi `SatisTalepleriPanel`'e (AdminDanismanCockpit.jsx
 * içinde render edilir); satır içi durum değişimi bilinçli olarak
 * KALDIRILDI (LeadDetayModal'daki Durum select'i aynı ihtiyacı karşılıyor).
 * Eski adres /admin/cockpit'e (query string korunarak) redirect edilir.
 *
 * Backend prefix: /api/danisman/cockpit, /api/{admin|danisman}/leads
 */

import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  leadStatusLabel,
  leadStatusColors,
} from '../../hooks/useLeadStatusOptions';
import { danismanOptionsList } from '../../hooks/useDanismanlarOptions';
import { useAccountingBanks } from '../../api/queries/accounting';
// FAZ 1 satış sadeleştirme (2026-09-25) — satış anında POS hesabı tahsilat
// hesabı olarak KABUL EDİLMEZ, yalnızca kasa/banka listelenir.
import { isPosAccountType } from '../../lib/posNetAmount';
// FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan, bkz.
// components/sales/PaymentPlanFields.jsx dosya başı yorumu.
import PaymentPlanFields from '../sales/PaymentPlanFields';
import { Skeleton } from '../ui/skeleton';
import Pagination from './Pagination';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ui/dialog';
import {
  X,
  ChevronDown,
  Search,
  Bell,
  Clock,
  AlarmClock,
  CheckCircle2,
  RefreshCw,
  Phone,
  MessageCircle,
  AlertTriangle,
  Pencil,
  History,
  Send,
  UserCheck,
  ExternalLink,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const INPUT_CLS =
  'w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none';
export const LBL_CLS = 'text-[10px] font-bold text-[var(--text-muted)] mb-1 block uppercase tracking-wide';

export { leadStatusLabel, leadStatusColors };
export { useLeadStatusOptions } from '../../hooks/useLeadStatusOptions';

// ─── Tarih / para yardımcıları ────────────────────────────────────────────────
//
// Backend'deki tarih alanlarının İKİ farklı kaynağı var:
//  1) Sistem üretimi zaman damgaları — her zaman gerçek UTC + offset/'Z' içerir.
//  2) Kullanıcının datetime-local input'undan girdiği alanlar (hatirlatma_tarihi,
//     görüşme "tarih" alanı) offset İÇERMEZ — TR yerel saat olarak saklanır.
// Format fonksiyonları bu yüzden offset varlığına göre dallanır.

function hasTzOffset(s) {
  return /[Zz]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s);
}

/** ISO tarih + saat string'ini "dd.MM.yyyy HH:mm" biçiminde döner. */
export function fmtDateTime(d) {
  if (!d) return '-';
  const s = String(d).trim();
  if (!hasTzOffset(s)) {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (m) {
      const [, y, mo, da, h, mi] = m;
      return `${da}.${mo}.${y} ${h}:${mi}`;
    }
  }
  try {
    return new Date(s).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(d);
  }
}

/** ISO tarih string'ini "dd.MM.yyyy" biçiminde döner (saat gösterilmez). */
export function fmtDate(d) {
  if (!d) return '-';
  const s = String(d).trim();
  if (!hasTzOffset(s)) {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const [, y, mo, da] = m;
      return `${da}.${mo}.${y}`;
    }
  }
  try {
    return new Date(s).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return String(d);
  }
}

/** Şu anki lokal saati datetime-local input'una uygun formatta döner. */
export function nowLocalDatetimeInput() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return (
    now.getFullYear() +
    '-' + pad(now.getMonth() + 1) +
    '-' + pad(now.getDate()) +
    'T' + pad(now.getHours()) +
    ':' + pad(now.getMinutes())
  );
}

export function fmtTRY(amount) {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(amount);
}

/**
 * TR cep telefonunu WhatsApp (wa.me) linki için E.164 formatına (90XXXXXXXXXX)
 * normalize eder. Güvenle normalize edilemiyorsa null döner — çağıran taraf
 * bozuk link üretmemek için WhatsApp ikonunu GÖSTERMEMELİDİR.
 */
export function normalizeTrPhoneForWhatsApp(raw) {
  if (!raw) return null;
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 12 && digits.startsWith('90')) return digits;
  if (digits.length === 11 && digits.startsWith('0')) return `90${digits.slice(1)}`;
  if (digits.length === 10 && digits.startsWith('5')) return `90${digits}`;
  return null;
}

// ─── Ortak hata/retry bloğu (Faz 1 CRM deseni) ───────────────────────────────

export function ErrorRetryCrm({ onRetry, tr }) {
  return (
    <div
      className="p-3 rounded-xl flex items-center justify-between gap-3"
      style={{ backgroundColor: 'rgba(160,61,44,0.06)', border: '1px solid rgba(160,61,44,0.25)' }}
    >
      <span className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--crm-danger)' }}>
        <AlertTriangle size={16} /> {tr('Bağlantı koptu — tekrar dene', 'Connection lost — try again')}
      </span>
      <button
        onClick={onRetry}
        className="px-3 py-1.5 rounded-lg text-xs font-bold text-white flex items-center gap-1 flex-shrink-0"
        style={{ backgroundColor: 'var(--crm-danger)' }}
      >
        <RefreshCw size={13} /> {tr('Tekrar Dene', 'Retry')}
      </button>
    </div>
  );
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

export function StatusBadge({ status, options }) {
  const label = leadStatusLabel(status, options);
  const { bg, text, border } = leadStatusColors(status);
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${bg} ${text} ${border}`}
    >
      {status === 'donusturuldu'
        ? <CheckCircle2 size={12} />
        : <span className={`w-1.5 h-1.5 rounded-full ${text.replace('text-', 'bg-')}`} />}
      {label}
    </span>
  );
}

// ─── Ödeme Detay Modalı ───────────────────────────────────────────────────────

// Faz D — `pencere_durumu` (gecikmis|bugun|yaklasan) rozet/renk eşlemesi.
// Backend'in satır bazlı etiketiyle AYNI 3 değer (bkz.
// routes/danisman_cockpit.py::_odeme_pencere_durumu). Bilinmeyen/eksik bir
// değer güvenli varsayılan olarak "gecikmis" stiline düşer (geriye dönük
// uyumlu — eski kayıtlarda alan olmayabilir).
const PENCERE_DURUMU_STYLES = {
  gecikmis: { rowBg: 'bg-rose-50', badgeBg: 'bg-rose-100', badgeText: 'text-rose-700' },
  bugun: { rowBg: 'bg-amber-50', badgeBg: 'bg-amber-100', badgeText: 'text-amber-700' },
  yaklasan: { rowBg: 'bg-cyan-50', badgeBg: 'bg-cyan-100', badgeText: 'text-cyan-700' },
};

function pencereDurumuLabel(durum, tr) {
  if (durum === 'bugun') return tr('Bugün', 'Today');
  if (durum === 'yaklasan') return tr('Yaklaşan', 'Upcoming');
  return tr('Gecikmiş', 'Overdue');
}

export function OdemeModal({ items, adet, toplam, lang, onClose, activeTab = 'varsayilan', onTabChange, tabLoading }) {
  const tr = (t, e) => (lang === 'tr' ? t : e);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl p-5 max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-bold text-base">{tr('Ödemesi Gelenler', 'Upcoming Payments')}</h3>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              {adet} {tr('adet', 'items')} — {fmtTRY(toplam)} {tr('ödenmeyen toplam', 'unpaid total')}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label={tr('Kapat', 'Close')}>
            <X size={18} />
          </button>
        </div>

        {/* Faz D — sekme: Gecikmiş+Bugün (eski davranış) / Yaklaşan (7 gün,
            ?pencere=yaklasan). onTabChange verilmezse (eski çağıran) sekme
            gizlenir — bu bileşen geriye dönük uyumlu kalır. */}
        {onTabChange && (
          <div className="flex gap-1.5 mb-3 p-1 rounded-xl bg-gray-100 w-fit">
            <button
              type="button"
              onClick={() => activeTab !== 'varsayilan' && onTabChange('varsayilan')}
              disabled={tabLoading}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 ${
                activeTab === 'varsayilan' ? 'bg-white text-rose-700 shadow-sm' : 'text-[var(--text-muted)]'
              }`}
            >
              {tr('Gecikmiş + Bugün', 'Overdue + Today')}
            </button>
            <button
              type="button"
              onClick={() => activeTab !== 'yaklasan' && onTabChange('yaklasan')}
              disabled={tabLoading}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 ${
                activeTab === 'yaklasan' ? 'bg-white text-cyan-700 shadow-sm' : 'text-[var(--text-muted)]'
              }`}
            >
              {tr('Yaklaşan (7 gün)', 'Upcoming (7 days)')}
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th scope="col" className="text-left p-2 font-bold">{tr('Öğrenci', 'Student')}</th>
                <th scope="col" className="text-left p-2 font-bold">{tr('No', 'No')}</th>
                <th scope="col" className="text-center p-2 font-bold">{tr('Taksit', 'Inst.')}</th>
                <th scope="col" className="text-center p-2 font-bold">{tr('Vade', 'Due')}</th>
                <th scope="col" className="text-right p-2 font-bold">{tr('Tutar', 'Amount')}</th>
                <th scope="col" className="text-center p-2 font-bold">{tr('Durum', 'Status')}</th>
                <th scope="col" className="text-center p-2 font-bold">{tr('Pencere', 'Window')}</th>
                <th scope="col" className="text-center p-2 font-bold">{tr('Gecikme', 'Overdue')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const durum = PENCERE_DURUMU_STYLES[it.pencere_durumu] ? it.pencere_durumu : 'gecikmis';
                const style = PENCERE_DURUMU_STYLES[durum];
                return (
                  <tr
                    key={i}
                    className={`border-t border-[var(--border-light)] ${style.rowBg}`}
                  >
                    <td className="p-2 font-semibold">{it.ogrenci_adi}</td>
                    <td className="p-2 text-xs text-[var(--text-muted)]">{it.ogrenci_no}</td>
                    <td className="p-2 text-center">{it.taksit_no}</td>
                    <td className="p-2 text-center text-xs">{fmtDate(it.due_date)}</td>
                    <td className="p-2 text-right font-bold">{fmtTRY(it.amount)}</td>
                    <td className="p-2 text-center">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                          it.status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {it.status === 'paid' ? tr('Ödendi', 'Paid') : tr('Bekliyor', 'Pending')}
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${style.badgeBg} ${style.badgeText}`}>
                        {pencereDurumuLabel(durum, tr)}
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      {it.gecikme_gun > 0 ? (
                        <span className="text-xs font-bold text-rose-600">{it.gecikme_gun} {tr('gün', 'days')}</span>
                      ) : (
                        <span className="text-xs text-[var(--text-muted)]">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-4 text-center text-sm text-[var(--text-muted)]">
                    {tr('Kayıt yok', 'No records')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Son Aramalar Sekmesi ─────────────────────────────────────────────────────

export const SonAramalarTab = forwardRef(function SonAramalarTab(
  { lang, authHeaders, statusOptions, onLeadClick },
  ref
) {
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(50);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await axios.get(
        `${API}/danisman/cockpit/son-aramalar?limit=${limit}&offset=${offset}`,
        { headers: authHeaders() }
      );
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      setError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, limit, lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Kaydet sonrası sayfanın modal'a onSaved ile geçtiği refetch tetikleyicisi
  // — offset/arama gibi mevcut bağlamı KORUYARAK yalnızca veriyi tazeler
  // (bkz. LeadDetayModal onSaved sözleşmesi, tam remount YOK).
  useImperativeHandle(ref, () => ({ refetch: fetchData }), [fetchData]);

  return (
    <div className="card-clay overflow-hidden">
      {loading && (
        <div className="p-4 space-y-2">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      )}
      {!loading && error && <div className="p-4"><ErrorRetryCrm onRetry={fetchData} tr={tr} /></div>}
      {!loading && !error && (
        <div className="divide-y divide-[var(--border-light)]">
          {items.map((it, i) => (
            <div key={it.gorusme_id || i} className="flex items-start gap-3 p-4 hover:bg-gray-50">
              <div className="flex-shrink-0">
                <span className="inline-block px-2 py-0.5 rounded-lg bg-gray-100 text-xs font-bold text-[var(--text-muted)]">
                  {fmtDateTime(it.tarih)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => onLeadClick({ lead_id: it.lead_id, ad_soyad: it.ad_soyad })}
                    className="font-bold text-[var(--brand-primary)] hover:underline text-sm"
                  >
                    {it.ad_soyad}
                  </button>
                  <StatusBadge status={it.status} options={statusOptions} />
                </div>
                {it.not && (
                  <p className="text-xs text-[var(--text-muted)] mt-0.5 truncate">{it.not}</p>
                )}
              </div>
              <div className="text-right text-xs text-[var(--text-muted)] flex-shrink-0">
                <div>{it.danisman_adi}</div>
                <div className="mt-0.5">{it.egitim}</div>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="p-6 text-center text-sm text-[var(--text-muted)]">
              {tr('Görüşme yok', 'No calls found')}
            </div>
          )}
        </div>
      )}
      <Pagination
        total={total}
        limit={limit}
        offset={offset}
        loading={loading}
        tr={tr}
        onPageChange={(o) => setOffset(o)}
        onLimitChange={(s) => { setLimit(s); setOffset(0); }}
      />
    </div>
  );
});

// ─── Data Detay Geçmiş Sekmesi ────────────────────────────────────────────────

export const DataGecmisTab = forwardRef(function DataGecmisTab(
  { lang, authHeaders, statusOptions, onLeadClick, initialLeadId },
  ref
) {
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const toast = useToast();
  const [q, setQ] = useState('');
  // LeadDetayModal'daki "Data Geçmişi" linki ?lead_id=... ile buraya deep-link
  // yapar — backend /danisman/cockpit/data-gecmisi zaten lead_id parametresini
  // destekliyor (bkz. routes/danisman_cockpit.py). Kullanıcı "×" ile temizleyebilir.
  const [leadIdFilter, setLeadIdFilter] = useState(initialLeadId || '');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(50);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const p = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (q) p.set('q', q);
      if (leadIdFilter) p.set('lead_id', leadIdFilter);
      const r = await axios.get(`${API}/danisman/cockpit/data-gecmisi?${p.toString()}`, {
        headers: authHeaders(),
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      setError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, offset, limit, leadIdFilter, lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // bkz. SonAramalarTab'daki aynı desen — arama/offset/lead_id filtresi
  // korunarak yalnızca veri tazelenir.
  useImperativeHandle(ref, () => ({ refetch: fetchData }), [fetchData]);

  const filteredLeadName = leadIdFilter ? items.find((it) => it.lead_id === leadIdFilter)?.ad_soyad : null;

  return (
    <div className="space-y-3">
      {leadIdFilter && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[var(--brand-primary)]/10 border-2 border-[var(--brand-primary)] w-fit">
          <span className="text-xs font-bold text-[var(--brand-primary)]">
            {tr('Filtrelenen lead', 'Filtered lead')}: {filteredLeadName || leadIdFilter}
          </span>
          <button
            type="button"
            onClick={() => { setLeadIdFilter(''); setOffset(0); }}
            aria-label={tr('Filtreyi temizle', 'Clear filter')}
            className="text-[var(--brand-primary)] hover:opacity-70"
          >
            <X size={13} />
          </button>
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setOffset(0); fetchData(); } }}
            placeholder={tr('Arama...', 'Search...')}
            className={INPUT_CLS + ' pl-9'}
          />
        </div>
        <span className="text-sm text-[var(--text-muted)] font-semibold">
          {total} {tr('kayıt', 'entries')}
        </span>
      </div>

      <div className="card-clay overflow-hidden">
        {loading && (
          <div className="p-4 space-y-2">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        )}
        {!loading && error && <div className="p-4"><ErrorRetryCrm onRetry={fetchData} tr={tr} /></div>}
        {!loading && !error && (
          <div className="divide-y divide-[var(--border-light)]">
            {items.map((it, i) => (
              <div key={it.gorusme_id || i} className="flex items-start gap-3 p-4 hover:bg-gray-50">
                <span className="flex-shrink-0 text-xs font-bold bg-gray-100 text-[var(--text-muted)] px-2 py-0.5 rounded-lg">
                  {fmtDateTime(it.tarih || it.islem_tarihi)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => onLeadClick({ lead_id: it.lead_id, ad_soyad: it.ad_soyad })}
                      className="font-bold text-[var(--brand-primary)] hover:underline text-sm"
                    >
                      {it.ad_soyad}
                    </button>
                    <StatusBadge status={it.status} options={statusOptions} />
                  </div>
                  {it.not && (
                    <p className="text-xs text-[var(--text-muted)] mt-0.5">{it.not}</p>
                  )}
                </div>
                <div className="text-right text-xs text-[var(--text-muted)] flex-shrink-0">
                  <div>{it.danisman_adi}</div>
                  <div>{it.egitim}</div>
                </div>
              </div>
            ))}
            {items.length === 0 && (
              <div className="p-6 text-center text-sm text-[var(--text-muted)]">
                {tr('Görüşme geçmişi yok', 'No history found')}
              </div>
            )}
          </div>
        )}
        <Pagination
          total={total}
          limit={limit}
          offset={offset}
          loading={loading}
          tr={tr}
          onPageChange={(o) => setOffset(o)}
          onLimitChange={(s) => { setLimit(s); setOffset(0); }}
        />
      </div>
    </div>
  );
});

// ─── Ertele dropdown (15/30/60 dakika) ───────────────────────────────────────
//
// "Mevcut 15 dakika erteleme mantığı" — davranış AYNEN korunuyor (15/30/60
// dakika seçenekleri, POST /hatirlatmalar/{id}/ertele). Yalnızca görsel
// kabuk (buton rengi) çağıran bileşene göre değişebilir.

export function ErteleDropdown({ gorusmeId, tr, erteliyor, ertele, className }) {
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className={`relative inline-block flex-shrink-0 ${className || ''}`} ref={dropRef}>
      <button
        onClick={() => setDropOpen((o) => !o)}
        disabled={erteliyor === gorusmeId}
        className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-blue-500 text-white text-xs font-bold border-2 border-blue-600 hover:bg-blue-600 disabled:opacity-50"
      >
        <AlarmClock size={13} />
        15 {tr('Dakika', 'Min')}
        <ChevronDown size={12} />
      </button>
      <AnimatePresence>
        {dropOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="absolute right-0 top-full mt-1 bg-white border-2 border-[var(--border-light)] rounded-xl shadow-lg z-20 min-w-[120px]"
          >
            {[15, 30, 60].map((dk) => (
              <button
                key={dk}
                onClick={() => { ertele(gorusmeId, dk); setDropOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-sm font-semibold hover:bg-gray-50 first:rounded-t-xl last:rounded-b-xl"
              >
                {dk} {tr('dakika', 'min')}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Hatırlatma Satırı (liste görünümü) ──────────────────────────────────────

export function HatirlatmaRow({ item, lang, statusOptions, onLeadClick, erteliyor, ertele }) {
  const tr = (t, e) => (lang === 'tr' ? t : e);
  return (
    <div className="flex items-center gap-3 p-3 hover:bg-gray-50 border-b border-[var(--border-light)] last:border-0">
      <div className="flex-shrink-0">
        <Clock size={14} className="text-[var(--text-muted)]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-lg">
            {fmtDateTime(item.hatirlatma_tarihi)}
          </span>
          <button
            onClick={() => onLeadClick({ lead_id: item.lead_id, ad_soyad: item.ad_soyad })}
            className="font-bold text-[var(--brand-primary)] hover:underline text-sm"
          >
            {item.ad_soyad}
          </button>
          <StatusBadge status={item.status} options={statusOptions} />
        </div>
        {item.aciklama && (
          <p className="text-xs text-[var(--text-muted)] mt-0.5">{item.aciklama}</p>
        )}
        <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{item.danisman_adi}</p>
      </div>
      <ErteleDropdown gorusmeId={item.gorusme_id} tr={tr} erteliyor={erteliyor} ertele={ertele} />
    </div>
  );
}

// ─── Hatırlatma Tablosu (6.png — Danışman Paneli, Eskiyen bloğu) ────────────

function HatirlatmaTablosu({ items, tr, isAdmin, onLeadClick, erteliyor, ertele }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="text-left p-3 font-bold w-10">#</th>
            <th scope="col" className="text-left p-3 font-bold">{tr('Ad Soyad', 'Name')}</th>
            <th scope="col" className="text-left p-3 font-bold">{tr('Açıklama', 'Description')}</th>
            <th scope="col" className="text-right p-3 font-bold">{tr('Ertele', 'Snooze')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={it.gorusme_id || idx} className="border-t border-[var(--border-light)] hover:bg-gray-50">
              <td className="p-3 text-xs text-[var(--text-muted)]">{idx + 1}</td>
              <td className="p-3">
                <button
                  onClick={() => onLeadClick({ lead_id: it.lead_id, ad_soyad: it.ad_soyad })}
                  className="font-bold text-[var(--brand-primary)] hover:underline text-left"
                >
                  {it.ad_soyad}
                </button>
              </td>
              <td className="p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-lg flex-shrink-0">
                    {fmtDateTime(it.hatirlatma_tarihi)}
                  </span>
                  {isAdmin && it.danisman_adi && (
                    <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-lg flex-shrink-0">
                      {it.danisman_adi}
                    </span>
                  )}
                  {it.aciklama && (
                    <span className="text-sm text-[var(--text-main)]">{it.aciklama}</span>
                  )}
                </div>
              </td>
              <td className="p-3 text-right">
                <ErteleDropdown gorusmeId={it.gorusme_id} tr={tr} erteliyor={erteliyor} ertele={ertele} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * HatirlatmalarPanel — GET /danisman/cockpit/hatirlatmalar?scope=... çeker,
 * skeleton/boş/hata+retry durumlarını yönetir.
 *
 * variant='table' → 6.png (Danışman Paneli, Eskiyen bloğu): # / Ad Soyad /
 *   Açıklama / Ertele.
 * variant='list'  → önceki liste görünümü (Datalar / Yaklaşan Hatırlatmalar
 *   sekmesi).
 */
export const HatirlatmalarPanel = forwardRef(function HatirlatmalarPanel({
  scope,
  variant = 'list',
  limit: initialLimit = 50,
  lang,
  authHeaders,
  isAdmin,
  onLeadClick,
  statusOptions = [],
  emptyText,
}, ref) {
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [erteliyor, setErteliyor] = useState(null);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(initialLimit);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await axios.get(
        `${API}/danisman/cockpit/hatirlatmalar?scope=${scope}&limit=${limit}&offset=${offset}`,
        { headers: authHeaders() }
      );
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      setError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, limit, offset, lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Erken return'lerden (loading/error/empty) ÖNCE çağrılmalı — hook kuralları
  // gereği koşulsuz her render'da çalışır. bkz. SonAramalarTab'daki aynı desen.
  // NOT: refetch mevcut offset/limit'i KORUR — kayıt sonrası sayfa sıfırlanmaz.
  useImperativeHandle(ref, () => ({ refetch: fetchData }), [fetchData]);

  const ertele = async (gorusmeId, dakika) => {
    setErteliyor(gorusmeId);
    try {
      await axios.post(
        `${API}/danisman/cockpit/hatirlatmalar/${gorusmeId}/ertele`,
        { dakika },
        { headers: authHeaders() }
      );
      toast.success(tr(`${dakika} dakika ertelendi`, `Snoozed ${dakika} minutes`));
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setErteliyor(null);
    }
  };

  if (loading) {
    return (
      <div className="p-4 space-y-2">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }
  if (error) {
    return <div className="p-4"><ErrorRetryCrm onRetry={fetchData} tr={tr} /></div>;
  }
  if (items.length === 0) {
    return (
      <p className="p-4 text-sm text-center text-[var(--text-muted)]">
        {emptyText || tr('Kayıt yok', 'No records')}
      </p>
    );
  }

  const onPageChange = (o) => setOffset(o);
  const onLimitChange = (s) => { setLimit(s); setOffset(0); };

  if (variant === 'table') {
    return (
      <>
        <HatirlatmaTablosu
          items={items}
          lang={lang}
          tr={tr}
          isAdmin={isAdmin}
          onLeadClick={onLeadClick}
          erteliyor={erteliyor}
          ertele={ertele}
        />
        <Pagination
          total={total}
          limit={limit}
          offset={offset}
          loading={loading}
          tr={tr}
          pageSizeOptions={[25, 50, 100]}
          onPageChange={onPageChange}
          onLimitChange={onLimitChange}
        />
      </>
    );
  }

  return (
    <>
      {items.map((it) => (
        <HatirlatmaRow
          key={it.gorusme_id}
          item={it}
          lang={lang}
          statusOptions={statusOptions}
          onLeadClick={onLeadClick}
          erteliyor={erteliyor}
          ertele={ertele}
        />
      ))}
      <Pagination
        total={total}
        limit={limit}
        offset={offset}
        loading={loading}
        tr={tr}
        pageSizeOptions={[25, 50, 100]}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
      />
    </>
  );
});

// ─── Satış Taleplerim Paneli (danışman "Satışa Hazır İşaretle" takibi) ───────
//
// Faz 6 (bkz. proje-yoneticisi görev talimatı, 2026-08-13 — Atanan Leadler
// kaldırma turu): eskiden AdminAssignedLeads.jsx içinde "Satış Taleplerim"
// başlığıyla yaşayan katlanabilir liste BİREBİR aynı mantıkla (aynı endpoint,
// aynı durum rozetleri) buraya taşındı. AdminDanismanCockpit.jsx (Danışman
// Paneli) bu paneli Ödemesi Gelenler özetinin ALTINA, Hatırlatmalar'ın
// ÜSTÜNE, varsayılan KAPALI render eder.
//
// GET /danisman/satis-talepleri — danışman rolünde yalnızca kendi talepleri
// döner (backend _build_talep_filter); admin/superadmin bu uç noktayı
// override'sız çağırırsa TÜM talepler döner ("benim" semantiği kaybolur) —
// bu yüzden panel SADECE danışman rolünde render edilmelidir (çağıran taraf
// koşullu render eder, bkz. AdminDanismanCockpit.jsx).
export const SatisTalepleriPanel = forwardRef(function SatisTalepleriPanel(
  { lang, authHeaders, defaultOpen = false, id },
  ref
) {
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(defaultOpen);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/danisman/satis-talepleri?page_size=50`, { headers: authHeaders() });
      setItems(r.data.talepler || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useImperativeHandle(ref, () => ({ refetch: fetchData }), [fetchData]);

  const pendingCount = items.filter((t) => t.durum === 'bekliyor').length;

  // "islemde" ve "onay_hata" danışman için nötr gösterilir — muhasebe hatası
  // detayı (retry vb.) admin'in derdi, danışmana teknik ayrıntı sızdırılmaz
  // (bkz. eski satisTalepDurumBadge, AdminAssignedLeads.jsx).
  const durumBadge = (durum) => {
    if (durum === 'bekliyor') return { cls: 'bg-amber-100 text-amber-700', label: tr('Onay Bekliyor', 'Pending') };
    if (durum === 'onaylandi') return { cls: 'bg-emerald-100 text-emerald-700', label: tr('Onaylandı', 'Approved') };
    if (durum === 'islemde' || durum === 'onay_hata') {
      return { cls: 'bg-slate-100 text-slate-600', label: tr('İşlem Sürüyor', 'In Progress') };
    }
    return { cls: 'bg-rose-100 text-rose-700', label: tr('Reddedildi', 'Rejected') };
  };

  return (
    <div id={id} className="bg-white p-4 mb-5" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="satis-taleplerim-liste"
        className="w-full flex items-center justify-between text-left"
      >
        <span className="font-bold text-sm flex items-center gap-2">
          <Send size={15} className="text-[var(--brand-primary)]" />
          {tr('Satış Taleplerim', 'My Sale Requests')}
          {pendingCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
              {pendingCount}
            </span>
          )}
        </span>
        <ChevronDown size={16} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div id="satis-taleplerim-liste" className="mt-3 space-y-2">
          {loading && <p className="text-xs text-[var(--text-muted)]">{tr('Yükleniyor...', 'Loading...')}</p>}
          {!loading && items.length === 0 && (
            <p className="text-xs text-[var(--text-muted)]">{tr('Henüz satış talebiniz yok', 'No sale requests yet')}</p>
          )}
          {items.map((t) => {
            const badge = durumBadge(t.durum);
            return (
              <div key={t.talep_id} className="flex items-center justify-between gap-2 p-2 rounded-lg border border-[var(--border-light)] text-xs">
                <div>
                  <span className="font-bold">{t.lead_ozet?.ad_soyad || t.lead_id}</span>
                  {t.durum === 'reddedildi' && t.red_gerekcesi && (
                    <p className="text-[var(--text-muted)] mt-0.5">{tr('Gerekçe', 'Reason')}: {t.red_gerekcesi}</p>
                  )}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${badge.cls}`}>{badge.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

// ─── Lead Detay Modalı (Faz 4 — 9.png, ortada Dialog) ────────────────────────
//
// LeadDetayModal — eskiden `GorusmeSheet` + `LeadDetailSheet` (bu dosya) ve
// `AdminAssignedLeads.jsx` içindeki üçüncü kopya `GorusmePaneli`'nin YERİNE
// geçen TEK bileşen. AdminDanismanCockpit.jsx (Datalar bloğu dahil, bkz.
// DatalarBlock.jsx) bunu import eder — Faz 6'da AdminAssignedLeads.jsx
// kaldırıldı (bkz. dosya başı yorumu), o sayfanın Dönüştür / Satışa Hazır
// İşaretle / Kimlik Oluştur / Profil eylemleri aşağıdaki "İşlemler"
// bölümüne taşındı.
//
// Rol davranışı (KRİTİK): admin/superadmin Danışman/Şehir/Eğitim Durumu/
// Cinsiyet/Meslek alanlarını DÜZENLEYEBİLİR (PUT /admin/leads/{lead_id} —
// zaten require_admin korumalı). danisman rolünde bu 5 alan SALT OKUNUR düz
// metin olarak render edilir (DOM'a select/input hiç girmez) — danışmanın
// lead'i başkasına atama / bilgilerini değiştirme yetkisi yok.
//
// Görüşme kaydı (Açıklama + Durum + Hatırlatma) her iki rolde de düzenlenebilir
// ve mevcut POST /{admin|danisman}/leads/{lead_id}/gorusmeler akışını AYNEN
// kullanır. 9.png'de kanal/tarih alanı YOK — backend'de ikisi de opsiyonel
// (kanal null geçilebilir, tarih verilmezse backend "şimdi" ile doldurur) —
// bu yüzden burada da gönderilmiyor. "Data Geçmişi" linki tüm danışmanlar
// genelindeki geçmişe gider (bkz. DatalarBlock.jsx — Data Detay Geçmiş
// sekmesi, /admin/cockpit?tab=data-gecmis&lead_id= ile filtrelenir).
//
// "Önceki Görüşmeler" (Faz 4 sonrası geri eklendi — reviewer bulgusu, 2026-08):
// Faz 4'te GorusmeSheet/LeadDetailSheet birleştirilirken görüşme notu
// DÜZENLEME (PUT .../gorusmeler/{gorusme_id}) ve DEĞİŞİKLİK GEÇMİŞİ
// (GET .../gorusmeler/{gorusme_id}/degisiklikler) yetenekleri sessizce
// düşmüştü. Aşağıdaki varsayılan-kapalı bölüm bunları eski Sheet'teki AYNI
// endpoint/yetki davranışıyla geri getirir (bkz. backend routes/leads.py
// _lead_gorusme_duzenle_core — danışman yalnızca KENDİ attığı görüşmeyi
// düzenleyebilir, admin hepsini; geçmiş okuma admin/danışman ikisine de açık).
//
// "Gönderilen SMS" linki BU TURDA render edilmiyor — message_log koleksiyonunda
// lead_id alanı yok, besleyecek veri yok. Alan eklendiğinde: aşağıdaki yorumun
// olduğu yere bir <a href="/admin/messages?lead_id=...">...</a> eklenecek.
export function LeadDetayModal({ lead, lang, statusOptions, authHeaders, isAdmin, currentUserId, onClose, onSaved }) {
  const tr = (t, e) => (lang === 'tr' ? t : e);
  // FAZ 1 karma ödeme turu (2026-09-25) — "sales.*" i18n metinleri artık
  // PaymentPlanFields kendi içinde `useLanguage()` ile çekiyor, burada ayrıca
  // `t()` gerekmiyor (eski FAZ 1 satış sadeleştirme deseninin aksine).
  const toast = useToast();
  const gorusmePrefix = isAdmin ? 'admin' : 'danisman';
  const aciklamaRef = useRef(null);
  // FAZ 1 satış sadeleştirme (2026-09-25) — "Öğrenciye Dönüştür" ADMIN-ONLY
  // (bkz. aşağıdaki openConvert notu); GET /admin/banks de require_admin,
  // bu yüzden `enabled: isAdmin` — danışman bu modalı açtığında 403 istek
  // hiç atılmaz.
  const { data: allBanks = [], isLoading: banksLoading } = useAccountingBanks({ enabled: isAdmin });
  const collectionBanks = allBanks.filter((b) => !isPosAccountType(b.account_type));

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);

  // Admin-only lookup verileri (danisman icin hic fetch edilmez)
  const [defs, setDefs] = useState({ sehir: [], meslek: [], egitim_durumu: [] });
  const [danismanlar, setDanismanlar] = useState([]);

  // Admin editable alan bloğu
  const [fDanismanId, setFDanismanId] = useState('');
  const [fSehir, setFSehir] = useState('');
  const [fEgitimDurumu, setFEgitimDurumu] = useState('');
  const [fCinsiyet, setFCinsiyet] = useState('');
  const [fMeslek, setFMeslek] = useState('');

  // Görüşme kaydı bloğu (her iki rolde de düzenlenebilir)
  const [aciklama, setAciklama] = useState('');
  const [durum, setDurum] = useState('');
  const [hatirlatmaVar, setHatirlatmaVar] = useState(false);
  const [hatirlatmaDate, setHatirlatmaDate] = useState('');
  const [hatirlatmaTime, setHatirlatmaTime] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Önceki Görüşmeler bölümü (varsayılan kapalı) — düzenleme + değişiklik geçmişi
  const [gecmisAcik, setGecmisAcik] = useState(false);
  const [gecmisList, setGecmisList] = useState([]);
  const [gecmisTotal, setGecmisTotal] = useState(0);
  const [gecmisLoading, setGecmisLoading] = useState(false);
  const [gecmisError, setGecmisError] = useState(false);
  const [gecmisFetched, setGecmisFetched] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editNot, setEditNot] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [historyOpenId, setHistoryOpenId] = useState(null);
  const [historyById, setHistoryById] = useState({});
  const [historyLoadingId, setHistoryLoadingId] = useState(null);
  const [historyErrorById, setHistoryErrorById] = useState({});

  // ── İşlemler bölümü (Faz 6 — Atanan Leadler kaldırma turu, bkz.
  // proje-yoneticisi görev talimatı 2026-08-13): eskiden AdminAssignedLeads.jsx
  // satır içi butonlarında yaşayan Dönüştür / Satışa Hazır İşaretle / Kimlik
  // Oluştur / Profil eylemleri BİREBİR aynı yetki/görünürlük kurallarıyla
  // buraya taşındı. Kurs listesi component mount'unda BİR KEZ çekilir (lead
  // değiştikçe YENİDEN çekilmez — public/paylaşılan veri).
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    axios.get(`${API}/courses?limit=100`).then((r) => setCourses(r.data.courses || [])).catch(() => {});
  }, []);

  // Danışman için: açık lead'e ait "bekliyor" durumunda satış talebi var mı —
  // varsa "Satışa Hazır İşaretle" yerine "Talep Gönderildi" gösterilir (bkz.
  // eski openTalepByLead haritası, AdminAssignedLeads.jsx).
  const [pendingSalesReq, setPendingSalesReq] = useState(null);

  useEffect(() => {
    if (!lead || isAdmin) { setPendingSalesReq(null); return; }
    let cancelled = false;
    axios.get(`${API}/danisman/satis-talepleri?durum=bekliyor&page_size=50`, { headers: authHeaders() })
      .then((r) => {
        if (cancelled) return;
        const found = (r.data.talepler || []).find((t) => t.lead_id === lead.lead_id);
        setPendingSalesReq(found || null);
      })
      .catch(() => { if (!cancelled) setPendingSalesReq(null); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.lead_id, isAdmin]);

  // Öğrenciye Dönüştür — admin-only (backend admin-only convert endpoint)
  const [convOpen, setConvOpen] = useState(false);
  const [convRows, setConvRows] = useState([]);
  const [cCourse, setCCourse] = useState('');
  const [cTur, setCTur] = useState('ana_egitim');
  const [cFiyat, setCFiyat] = useState('');
  const [converting, setConverting] = useState(false);
  // FAZ 1 karma ödeme turu (2026-09-25) — eski üçlü seçim (Peşin / Taksitli /
  // Ücretsiz) ikiye indi: "paid" (Peşinat + Kalan, bkz. PaymentPlanFields.jsx)
  // ve "free". Peşin tam tahsil / taksitli senaryoların ikisi de artık
  // PaymentPlanFields'in Peşinat+Kalan sözleşmesiyle temsil edilir.
  const [payMethod, setPayMethod] = useState('paid');
  const [discountPercent, setDiscountPercent] = useState(0);
  // FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan alanları/doğrulaması
  // artık paylaşılan components/sales/PaymentPlanFields.jsx tarafından
  // yönetilir (bkz. o dosyanın başı yorumu).
  const [paymentPlanFields, setPaymentPlanFields] = useState({});
  const [paymentPlanValid, setPaymentPlanValid] = useState(false);
  const [paymentPlanResetKey, setPaymentPlanResetKey] = useState(0);
  const convOverlayRef = useRef(null);
  // Alt modalı açan butonu tutar — modal kapanınca odak buraya geri döner
  // (bkz. proje-yoneticisi görev talimatı, Kusur 1: odak <body>'ye düşmesin).
  const convTriggerRef = useRef(null);

  // Satış talebi — danışman "Satışa Hazır İşaretle"
  const [satisOpen, setSatisOpen] = useState(false);
  const [satisCourseIds, setSatisCourseIds] = useState([]);
  const [satisFiyat, setSatisFiyat] = useState('');
  const [satisOdeme, setSatisOdeme] = useState('bilinmiyor');
  const [satisNot, setSatisNot] = useState('');
  const [satisSaving, setSatisSaving] = useState(false);
  const satisNotValid = satisNot.trim().length >= 10;
  // On kayit kisisel bilgileri — 25 Agustos 2026 (Recep karari): sihirbazin
  // 1. adim alanlari bu pencereye de eklendi. Mevcut kurs/fiyat/not alanlari
  // KALDIRILMADI (Recep "ikisi de kalsin" dedi).
  // Anahtarlar backend beyaz listesiyle BIREBIR ayni olmali
  // (routes/leads.py::ON_KAYIT_KISISEL_ALANLAR) — eslesmeyen anahtar sessizce
  // atilir ve alan hic kaydedilmemis gibi olur.
  const [satisKisisel, setSatisKisisel] = useState({});
  const setKisiselAlan = (k, v) => setSatisKisisel((prev) => ({ ...prev, [k]: v }));
  const satisOverlayRef = useRef(null);
  // Alt modalı açan butonu tutar — modal kapanınca odak buraya geri döner.
  const satisTriggerRef = useRef(null);

  const fetchDetail = useCallback(async () => {
    if (!lead) return;
    setDetailLoading(true);
    setDetailError(false);
    try {
      // NOT: path "/admin/leads/{id}" olsa da require_danisman ile korunuyor —
      // danışman kendi lead'ine erişebilir (backend routes/leads.py::get_lead).
      const r = await axios.get(`${API}/admin/leads/${lead.lead_id}`, { headers: authHeaders() });
      setDetail(r.data);
      setFDanismanId(r.data.atanan_personel_id || '');
      setFSehir(r.data.sehir || '');
      setFEgitimDurumu(r.data.egitim_durumu || '');
      setFCinsiyet(r.data.cinsiyet || '');
      setFMeslek(r.data.meslek || '');
      setDurum(
        statusOptions.some((o) => o.value === r.data.status)
          ? r.data.status
          : (statusOptions[0]?.value || 'yeni')
      );
    } catch (err) {
      setDetailError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setDetailLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead, lang]);

  // Lead değişince (modal yeni bir lead için açılınca YA DA `lead` null'a
  // çekilip modal kapanınca) formu + `detail`'i sıfırla.
  //
  // KRİTİK (bkz. bundle.js "Cannot read properties of null (reading
  // 'lead_id')" raporu): eskiden bu efekt `if (!lead) return;` ile erken
  // çıkıyordu, yani modal kapanırken (`lead` -> null) `detail` state'i bir
  // ÖNCEKİ lead'e ait veriyle stale kalıyordu. Dialog `open={!!lead}` ile
  // kapanış animasyonuna girerken component ağacı henüz unmount olmadığı
  // için aşağıdaki `{!detailLoading && !detailError && detail && (...)}`
  // bloğu (ve içindeki "Data Geçmişi" linkindeki `lead.lead_id`) bir render
  // daha çalışıp `lead` artık null olduğu için patlıyordu. Şimdi `detail`
  // (ve tüm form/gecmis/admin-editable state'i) `lead` null olduğunda da
  // HEMEN temizleniyor — `detail` null olunca o blok hiç render edilmiyor,
  // içindeki hiçbir `lead.` erişimi çalışmıyor. Aynı zamanda "modal kapanınca
  // form state'i sıfırlansın" gereksinimini de karşılıyor (bir sonraki lead
  // açıldığında öncekinin verisi görünmez).
  useEffect(() => {
    setDetail(null);
    setDetailError(false);
    setDetailLoading(false);
    setFDanismanId('');
    setFSehir('');
    setFEgitimDurumu('');
    setFCinsiyet('');
    setFMeslek('');
    setAciklama('');
    setDurum('');
    setHatirlatmaVar(false);
    setHatirlatmaDate('');
    setHatirlatmaTime('');
    setSaveError('');
    setGecmisAcik(false);
    setGecmisList([]);
    setGecmisTotal(0);
    setGecmisFetched(false);
    setGecmisError(false);
    setEditingId(null);
    setEditNot('');
    setEditError('');
    setHistoryOpenId(null);
    setHistoryById({});
    setHistoryErrorById({});
    setPendingSalesReq(null);
    setConvOpen(false);
    setConvRows([]);
    setCCourse('');
    setCTur('ana_egitim');
    setCFiyat('');
    setPayMethod('paid');
    setDiscountPercent(0);
    setPaymentPlanFields({});
    setPaymentPlanValid(false);
    setPaymentPlanResetKey((k) => k + 1);
    setSatisOpen(false);
    setSatisCourseIds([]);
    setSatisFiyat('');
    setSatisOdeme('bilinmiyor');
    setSatisNot('');
    // Yeni state de bu reset'e DAHIL olmali: aksi halde onceki lead'in
    // kisisel bilgileri bir sonraki lead'in penceresinde gorunur — modalin
    // "stale detail" regresyonuyla ayni sinif.
    setSatisKisisel({});
    if (lead) fetchDetail();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.lead_id]);

  // ── Önceki Görüşmeler: liste + düzenleme + değişiklik geçmişi ────────────
  //
  // Endpoint'ler eski GorusmeSheet/LeadDetailSheet ile AYNI (bkz. backend
  // routes/leads.py:992-1088, 1241-1326). Yetki: canEditGorusme admin için
  // her zaman true, danışman için yalnızca kendi attığı görüşmede true —
  // backend _lead_gorusme_duzenle_core zaten aynı kuralı 403 ile uygular,
  // burada UI'da tekrarlanıyor (yetkisiz butonu göstermemek için).

  const fetchGecmis = useCallback(async () => {
    if (!lead) return;
    setGecmisLoading(true);
    setGecmisError(false);
    try {
      const r = await axios.get(
        `${API}/${gorusmePrefix}/leads/${lead.lead_id}/gorusmeler?page=1&page_size=20`,
        { headers: authHeaders() }
      );
      setGecmisList(r.data.gorusmeler || []);
      setGecmisTotal(r.data.total ?? (r.data.gorusmeler || []).length);
      setGecmisFetched(true);
    } catch (err) {
      setGecmisError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setGecmisLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead, gorusmePrefix, lang]);

  const toggleGecmis = () => {
    const next = !gecmisAcik;
    setGecmisAcik(next);
    if (next && !gecmisFetched) fetchGecmis();
  };

  const canEditGorusme = (g) => isAdmin || g.personel_id === currentUserId;

  const startEditGorusme = (g) => {
    setEditingId(g.gorusme_id);
    setEditNot(g.not || '');
    setEditError('');
  };

  const cancelEditGorusme = () => {
    setEditingId(null);
    setEditNot('');
    setEditError('');
  };

  const saveEditGorusme = async (g) => {
    if (!editNot.trim() || editSaving) return;
    setEditSaving(true);
    setEditError('');
    try {
      const r = await axios.put(
        `${API}/${gorusmePrefix}/leads/${lead.lead_id}/gorusmeler/${g.gorusme_id}`,
        { not: editNot.trim() },
        { headers: authHeaders() },
      );
      setGecmisList((prev) => prev.map((x) => (x.gorusme_id === g.gorusme_id ? r.data.gorusme : x)));
      setEditingId(null);
      setEditNot('');
      toast.success(tr('Görüşme notu güncellendi', 'Note updated'));
    } catch (err) {
      // Hata durumunda modal/satır KAPANMAZ, kullanıcının yazdığı metin kaybolmaz.
      setEditError(getApiErrorMessage(err, lang));
    } finally {
      setEditSaving(false);
    }
  };

  const toggleHistory = (g) => {
    if (historyOpenId === g.gorusme_id) {
      setHistoryOpenId(null);
      return;
    }
    setHistoryOpenId(g.gorusme_id);
    if (!historyById[g.gorusme_id]) {
      setHistoryLoadingId(g.gorusme_id);
      setHistoryErrorById((prev) => ({ ...prev, [g.gorusme_id]: null }));
      axios
        .get(`${API}/${gorusmePrefix}/leads/${lead.lead_id}/gorusmeler/${g.gorusme_id}/degisiklikler`, {
          headers: authHeaders(),
        })
        .then((r) => setHistoryById((prev) => ({ ...prev, [g.gorusme_id]: r.data.degisiklikler || [] })))
        .catch((err) => setHistoryErrorById((prev) => ({ ...prev, [g.gorusme_id]: getApiErrorMessage(err, lang) })))
        .finally(() => setHistoryLoadingId(null));
    }
  };

  // Admin ise: Şehir/Eğitim Durumu/Meslek sözlükleri + danışman listesi
  useEffect(() => {
    if (!lead || !isAdmin) return;
    axios
      .get(`${API}/admin/definitions`, { headers: authHeaders() })
      .then((r) => {
        const all = r.data.definitions || [];
        setDefs({
          sehir: all.filter((d) => d.type === 'sehir'),
          meslek: all.filter((d) => d.type === 'meslek'),
          egitim_durumu: all.filter((d) => d.type === 'egitim_durumu'),
        });
      })
      .catch(() => {});
    // Yeniden atama amaçlı — HER ZAMAN aktif=tumu (bkz. hooks/useDanismanlarOptions.js
    // dosya başı yorumu). Pasif danışmanlar seçenekten ÇIKARILMAZ (lead zaten
    // pasif bir danışmana atanmış olabilir), select render'ında disablePasif
    // ile seçilemez yapılır.
    axios
      .get(`${API}/admin/danismanlar?include_admins=true&aktif=tumu`, { headers: authHeaders() })
      .then((r) => setDanismanlar(r.data.danismanlar || []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.lead_id, isAdmin]);

  // Modal açılınca ilk odak Açıklama alanına düşsün (içerik yüklendikten sonra)
  useEffect(() => {
    if (!detailLoading && !detailError && detail) {
      aciklamaRef.current?.focus();
    }
  }, [detailLoading, detailError, detail]);

  const handleSave = async () => {
    if (saving || !lead) return;
    setSaving(true);
    setSaveError('');
    try {
      if (isAdmin && detail) {
        const changed = {};
        if (fDanismanId !== (detail.atanan_personel_id || '')) {
          changed.atanan_personel_id = fDanismanId;
          const d = danismanlar.find((x) => x.user_id === fDanismanId);
          changed.atanan_personel_adi = d ? d.name : '';
        }
        if (fSehir !== (detail.sehir || '')) changed.sehir = fSehir;
        if (fEgitimDurumu !== (detail.egitim_durumu || '')) changed.egitim_durumu = fEgitimDurumu;
        if (fCinsiyet !== (detail.cinsiyet || '')) changed.cinsiyet = fCinsiyet;
        if (fMeslek !== (detail.meslek || '')) changed.meslek = fMeslek;
        if (Object.keys(changed).length > 0) {
          await axios.put(`${API}/admin/leads/${lead.lead_id}`, changed, { headers: authHeaders() });
        }
      }
      const body = { sonuc: durum, not: aciklama };
      if (hatirlatmaVar && hatirlatmaDate) {
        body.hatirlatma_tarihi = `${hatirlatmaDate}T${hatirlatmaTime || '00:00'}`;
      }
      await axios.post(`${API}/${gorusmePrefix}/leads/${lead.lead_id}/gorusmeler`, body, {
        headers: authHeaders(),
      });
      toast.success(tr('Kaydedildi', 'Saved'));
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      // Hata durumunda modal KAPANMAZ, kullanıcının yazdığı açıklama kaybolmaz.
      setSaveError(getApiErrorMessage(err, lang));
    } finally {
      setSaving(false);
    }
  };

  // ── İşlemler: Öğrenciye Dönüştür ──────────────────────────────────────────
  const openConvert = () => {
    convTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setConvRows([]);
    setCCourse('');
    setCTur('ana_egitim');
    setCFiyat('');
    setPayMethod('paid');
    setDiscountPercent(0);
    setPaymentPlanFields({});
    setPaymentPlanValid(false);
    setPaymentPlanResetKey((k) => k + 1);
    setConvOpen(true);
  };

  const addConvRow = () => {
    if (!cCourse) return;
    const c = courses.find((x) => x.course_id === cCourse);
    setConvRows((p) => [...p, { course_id: cCourse, tur: cTur, liste_fiyati: cFiyat || '0', title: c ? c.title : cCourse }]);
    setCCourse('');
    setCFiyat('');
  };

  // Eğitim seçilince fiyat alanı katalog fiyatıyla ön-doldurulur (admin yine
  // serbestçe düzenleyebilir) — bkz. eski AdminAssignedLeads.jsx handleCourseSelect.
  const handleConvCourseSelect = (courseId) => {
    setCCourse(courseId);
    if (!courseId) { setCFiyat(''); return; }
    const c = courses.find((x) => x.course_id === courseId);
    setCFiyat(c && c.price != null ? String(c.price) : '');
  };

  const doConvert = async () => {
    if (!lead || converting) return;
    // Defense-in-depth — buton zaten convertDisabled ile kilitleniyor.
    if (payMethod === 'paid' && !paymentPlanValid) return;
    setConverting(true);
    try {
      const cr = await axios.post(`${API}/admin/leads/${lead.lead_id}/convert`, {}, { headers: authHeaders() });
      const uid = cr.data.user_id;
      if (uid && convRows.length) {
        const isFree = payMethod === 'free';
        const payload = {
          courses: convRows.map((r) => ({ course_id: r.course_id, is_primary: r.tur === 'ana_egitim', price_override: Number(r.liste_fiyati || 0) })),
          assignment_type: isFree ? 'free' : 'paid',
          discount_percent: isFree ? 0 : Number(discountPercent || 0),
          // FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan alanları
          // (down_payment, down_payment_method/bank_id, installments/
          // first_due_date VEYA installment_plan, payment_method)
          // PaymentPlanFields tarafından hesaplanır (bkz. yukarıdaki onChange).
          // Ücretsizde bunların hiçbiri gönderilmez, yalnızca payment_method="free".
          ...(isFree ? { payment_method: 'free' } : paymentPlanFields),
        };
        await axios.post(`${API}/admin/students/${uid}/enroll-courses`, payload, { headers: authHeaders() });
      }
      toast.success(cr.data.already_converted
        ? tr('Eğitim/ödeme eklendi', 'Updated')
        : tr(`Öğrenci oluşturuldu. Geçici şifre: ${cr.data.temp_password}`, `Student created. Temp password: ${cr.data.temp_password}`));
      setConvOpen(false);
      fetchDetail();
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setConverting(false);
    }
  };

  // ── İşlemler: Satışa Hazır İşaretle ───────────────────────────────────────
  const openSatisModal = () => {
    satisTriggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setSatisCourseIds([]);
    setSatisFiyat('');
    setSatisOdeme('bilinmiyor');
    setSatisNot('');
    // Lead'de zaten bilinenleri on doldur: danisman ayni bilgiyi ikinci kez
    // yazmasin. Ad tek alanda tutuluyor, ilk bosluktan bolunuyor.
    const _ad = (detail?.ad_soyad || lead?.ad_soyad || '').trim();
    const _bosluk = _ad.indexOf(' ');
    setSatisKisisel({
      first_name: _bosluk > 0 ? _ad.slice(0, _bosluk) : _ad,
      last_name: _bosluk > 0 ? _ad.slice(_bosluk + 1) : '',
      email: detail?.email || lead?.email || '',
      phone: detail?.telefon || lead?.telefon || '',
      city: detail?.sehir || lead?.sehir || '',
    });
    setSatisOpen(true);
  };

  const toggleSatisCourse = (courseId) => {
    setSatisCourseIds((prev) => (prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId]));
  };

  const submitSatisTalebi = async () => {
    if (!lead || !satisNotValid || satisSaving) return;
    setSatisSaving(true);
    try {
      const onerilenEgitim = {
        course_ids: satisCourseIds,
        course_titles: satisCourseIds
          .map((id) => courses.find((c) => c.course_id === id)?.title)
          .filter(Boolean),
        onerilen_fiyat: satisFiyat ? Number(satisFiyat) : null,
        odeme_tercihi: satisOdeme,
      };
      const r = await axios.post(
        `${API}/danisman/leads/${lead.lead_id}/satis-talebi`,
        {
          not: satisNot.trim(),
          onerilen_egitim: onerilenEgitim,
          // Bos alanlar backend'de zaten ayiklaniyor; yine de gonderilmiyor
          // ki istek govdesi gereksiz sismesin.
          kisisel_bilgiler: Object.fromEntries(
            Object.entries(satisKisisel).filter(([, v]) => String(v || '').trim())
          ),
        },
        { headers: authHeaders() },
      );
      toast.success(tr('Satış talebi admin\'e iletildi', 'Sale request sent to admin'));
      setSatisOpen(false);
      setPendingSalesReq(r.data);
      if (onSaved) onSaved();
    } catch (err) {
      const code = err?.response?.data?.detail?.error_code;
      if (err?.response?.status === 409 && code === 'satis_talebi_zaten_acik') {
        toast.error(tr('Bu lead için zaten bekleyen bir talep var', 'A pending request already exists for this lead'));
        setSatisOpen(false);
        setPendingSalesReq(err.response.data.detail.talep || null);
      } else {
        toast.error(getApiErrorMessage(err, lang));
      }
    } finally {
      setSatisSaving(false);
    }
  };

  // İç içe modal (Convert / Satış): Radix Dialog'un kendi FocusScope/
  // escapeKeydown mekanizmasıyla İKİNCİ bir Radix Dialog'un ÇAKIŞMAMASI için
  // (bkz. proje-yoneticisi görev talimatı — "iç içe Dialog'da focus trap/ESC
  // davranışını doğrula, bozuksa alternatif kur") bu iki alt modal Radix
  // <Dialog> DEĞİL, düz konumlandırılmış bir overlay div'dir (eski
  // AdminAssignedLeads.jsx'teki Convert modalıyla AYNI desen). Focus trap +
  // Escape burada MANUEL uygulanır: document üzerinde CAPTURE fazında
  // dinlenir ki üstteki Radix Dialog'un kendi (bubble fazı) Escape
  // dinleyicisine olay hiç ULAŞMASIN (ana modal aynı anda kapanmasın).
  // NOT (bkz. proje-yoneticisi görev talimatı 2026-08-13, Kusur 1): bu iki
  // alt modal Radix'e geçirilmesi ayrı bir turda değerlendirilmeli — Radix
  // <Dialog> nested kullanımda DismissableLayer yığınını kendisi yönetiyor
  // ve aşağıdaki manuel focus-trap/Escape kodunu gereksiz kılabilir.
  const getFocusableIn = (overlay) => Array.from(
    overlay.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
  ).filter((el) => !el.disabled && el.offsetParent !== null);

  // İlk odaklama: SADECE convOpen/satisOpen false→true geçişinde çalışır.
  // KRİTİK — `converting`/`satisSaving` bilerek bağımlılık dışında bırakıldı:
  // eskiden bu iki state de effect'in dependency array'indeydi, bu yüzden
  // submit anında (false→true→false) effect yeniden tetiklenip odağı zorla
  // ilk forma (Eğitim select'i) geri çekiyordu — kullanıcı "Dönüştür" /
  // "Admin'e Gönder" butonuna basar basmaz odak kayboluyordu. Artık bu effect
  // yalnızca convOpen/satisOpen değiştiğinde (modal açılıp kapanınca) çalışır.
  useEffect(() => {
    const overlay = convOpen ? convOverlayRef.current : satisOpen ? satisOverlayRef.current : null;
    if (!overlay) return;
    getFocusableIn(overlay)[0]?.focus();
  }, [convOpen, satisOpen]);

  // Alt modal kapanınca odağı onu açan butona geri döndür (odak <body>'ye
  // düşmesin) — trigger element'i openConvert/openSatisModal'da yakalanır.
  useEffect(() => {
    if (convOpen) return;
    convTriggerRef.current?.focus();
    convTriggerRef.current = null;
  }, [convOpen]);
  useEffect(() => {
    if (satisOpen) return;
    satisTriggerRef.current?.focus();
    satisTriggerRef.current = null;
  }, [satisOpen]);

  // converting/satisSaving'in GÜNCEL değerini, effect'i yeniden kurmadan
  // (yani odağı yeniden konumlandırmadan) okuyabilmek için ref'e aynalanır.
  const convertingRef = useRef(converting);
  convertingRef.current = converting;
  const satisSavingRef = useRef(satisSaving);
  satisSavingRef.current = satisSaving;

  // Tab-trap + Escape: yalnızca convOpen/satisOpen'a bağlı — `converting`/
  // `satisSaving` submit sırasında değişse bile trap AÇIK KALIR ama odağı
  // yeniden konumlandırmaz (bkz. yukarıdaki ilk-odaklama effect'i).
  useEffect(() => {
    const overlay = convOpen ? convOverlayRef.current : satisOpen ? satisOverlayRef.current : null;
    if (!overlay) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (convOpen && !convertingRef.current) setConvOpen(false);
        if (satisOpen && !satisSavingRef.current) setSatisOpen(false);
        return;
      }
      if (e.key === 'Tab') {
        const items = getFocusableIn(overlay);
        if (items.length === 0) return;
        const firstEl = items[0];
        const lastEl = items[items.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [convOpen, satisOpen]);

  const waNumber = normalizeTrPhoneForWhatsApp(detail?.telefon);
  const { bg: durumBg, text: durumText, border: durumBorder } = leadStatusColors(durum);

  const infoJoin = (parts) => parts.map((p) => (p && String(p).trim()) || '–').join(' / ');

  // FAZ 1 karma ödeme turu (2026-09-25) — "Öğrenciye Dönüştür" modalı
  // hesapları. convRows boşsa (eğitimsiz dönüştürme) enroll-courses hiç
  // çağrılmaz — PaymentPlanFields o durumda da render edilir (totalAmount=0
  // iken kendi içinde erken çıkıp isValid:true döner, bkz. dosya başı yorumu).
  const convTotalListPrice = convRows.reduce((s, r) => s + Number(r.liste_fiyati || 0), 0);
  const convDiscountedTotal = convTotalListPrice * (1 - (Number(discountPercent) || 0) / 100);
  // PaymentPlanFields kendi geçerliliğini (peşinat/kalan/banka seçimi)
  // meta.isValid ile bildirir — "Dönüştür" butonu ücretli modda BUNA kilitlenir.
  const convertDisabled = converting || (payMethod === 'paid' && !paymentPlanValid);

  return (
    <Dialog open={!!lead} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-3xl w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto rounded-2xl border-2 border-[var(--border-light)] p-0">
        {/* Başlık şeridi */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-3 border-b-2 border-[var(--border-light)] flex-wrap">
          <DialogTitle className="font-bold text-base truncate">
            {detail?.ad_soyad || lead?.ad_soyad || '-'}
          </DialogTitle>
          {detail?.telefon && (
            <a
              href={`tel:${detail.telefon}`}
              className="flex items-center gap-1 text-sm font-semibold text-[var(--brand-primary)] hover:underline flex-shrink-0"
            >
              <Phone size={13} /> {detail.telefon}
            </a>
          )}
          {waNumber && (
            <a
              href={`https://wa.me/${waNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={tr('WhatsApp ile aç', 'Open in WhatsApp')}
              className="text-green-600 hover:text-green-700 flex-shrink-0"
            >
              <MessageCircle size={16} />
            </a>
          )}
          {detail && (
            <span className="text-xs font-semibold text-[var(--text-muted)] flex-shrink-0">
              ({tr('Arama Sayısı', 'Calls')}: {detail.arama_sayisi ?? 0})
            </span>
          )}
        </div>

        {detailLoading && (
          <div className="p-5 space-y-2">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        )}
        {!detailLoading && detailError && (
          <div className="p-5"><ErrorRetryCrm onRetry={fetchDetail} tr={tr} /></div>
        )}

        {!detailLoading && !detailError && detail && (
          <div className="px-5 pb-5 space-y-4">
            {/* Bilgi satırları */}
            <div className="rounded-xl border-2 border-[var(--border-light)] divide-y divide-[var(--border-light)]">
              <div className="flex items-start gap-3 p-3 text-sm">
                <span className="w-32 flex-shrink-0 font-bold text-[var(--text-muted)]">
                  {tr('Ders / Başvuru', 'Course / Source')}
                </span>
                <span className="font-semibold">
                  {infoJoin([detail.ders_ilgi, detail.reklam_turu, detail.reklam_firmasi])}
                </span>
              </div>
              <div className="flex items-start gap-3 p-3 text-sm">
                <span className="w-32 flex-shrink-0 font-bold text-[var(--text-muted)]">
                  {tr('Bilgiler', 'Info')}
                </span>
                <span className="font-semibold">
                  {infoJoin([detail.email, detail.sehir, detail.egitim_durumu, detail.meslek])}
                </span>
              </div>
            </div>

            {/* Alan bloğu — admin editable / danisman salt okunur */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div>
                <label className={LBL_CLS}>
                  {tr('Danışman', 'Advisor')}
                  {isAdmin && (
                    <span className="ml-1 normal-case font-semibold text-[var(--brand-primary)]">
                      {tr('Değiştir', 'Change')}
                    </span>
                  )}
                </label>
                {isAdmin ? (
                  <select value={fDanismanId} onChange={(e) => setFDanismanId(e.target.value)} className={INPUT_CLS}>
                    <option value="">{tr('Atanmadı', 'Unassigned')}</option>
                    {danismanOptionsList(danismanlar, tr, { disablePasif: true }).map((o) => (
                      <option key={o.key} value={o.value} disabled={o.disabled}>{o.label}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm font-semibold py-2">{detail.atanan_personel_adi || '–'}</p>
                )}
              </div>
              <div>
                <label className={LBL_CLS}>{tr('Şehir', 'City')}</label>
                {isAdmin ? (
                  <select value={fSehir} onChange={(e) => setFSehir(e.target.value)} className={INPUT_CLS}>
                    <option value="">{tr('— Seçiniz —', '— Select —')}</option>
                    {defs.sehir.map((d) => <option key={d.def_id} value={d.label_tr}>{d.label_tr}</option>)}
                  </select>
                ) : (
                  <p className="text-sm font-semibold py-2">{detail.sehir || '–'}</p>
                )}
              </div>
              <div>
                <label className={LBL_CLS}>{tr('Eğitim Durumu', 'Education')}</label>
                {isAdmin ? (
                  <select value={fEgitimDurumu} onChange={(e) => setFEgitimDurumu(e.target.value)} className={INPUT_CLS}>
                    <option value="">{tr('— Seçiniz —', '— Select —')}</option>
                    {defs.egitim_durumu.map((d) => <option key={d.def_id} value={d.label_tr}>{d.label_tr}</option>)}
                  </select>
                ) : (
                  <p className="text-sm font-semibold py-2">{detail.egitim_durumu || '–'}</p>
                )}
              </div>
              <div>
                <label className={LBL_CLS}>{tr('Cinsiyet', 'Gender')}</label>
                {isAdmin ? (
                  <select value={fCinsiyet} onChange={(e) => setFCinsiyet(e.target.value)} className={INPUT_CLS}>
                    <option value="">{tr('Belirsiz', 'N/A')}</option>
                    <option value="Erkek">{tr('Erkek', 'Male')}</option>
                    <option value="Kadin">{tr('Kadın', 'Female')}</option>
                  </select>
                ) : (
                  <p className="text-sm font-semibold py-2">{detail.cinsiyet || '–'}</p>
                )}
              </div>
              <div>
                <label className={LBL_CLS}>{tr('Meslek', 'Profession')}</label>
                {isAdmin ? (
                  <select value={fMeslek} onChange={(e) => setFMeslek(e.target.value)} className={INPUT_CLS}>
                    <option value="">{tr('— Seçiniz —', '— Select —')}</option>
                    {defs.meslek.map((d) => <option key={d.def_id} value={d.label_tr}>{d.label_tr}</option>)}
                  </select>
                ) : (
                  <p className="text-sm font-semibold py-2">{detail.meslek || '–'}</p>
                )}
              </div>
            </div>

            {/* Görüşme kaydı bloğu */}
            <div className="bg-gray-50 rounded-xl p-4 border-2 border-[var(--border-light)] space-y-3">
              <div>
                <label className={LBL_CLS}>{tr('Açıklama', 'Note')}</label>
                <textarea
                  ref={aciklamaRef}
                  value={aciklama}
                  onChange={(e) => setAciklama(e.target.value)}
                  rows={3}
                  className={INPUT_CLS + ' resize-none'}
                  placeholder={tr('Görüşme notunuzu girin...', 'Enter your call note...')}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className={LBL_CLS}>{tr('Durum', 'Status')}</label>
                  <select
                    value={durum}
                    onChange={(e) => setDurum(e.target.value)}
                    className={`${INPUT_CLS} font-bold ${durumBg} ${durumText} ${durumBorder}`}
                  >
                    {statusOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="flex items-center gap-1.5 text-[10px] font-bold text-[var(--text-muted)] mb-1 uppercase tracking-wide cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hatirlatmaVar}
                      onChange={(e) => setHatirlatmaVar(e.target.checked)}
                      className="accent-[var(--brand-primary)]"
                    />
                    <Bell size={12} />
                    {tr('Hatırlatma Var?', 'Set Reminder?')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={hatirlatmaDate}
                      onChange={(e) => setHatirlatmaDate(e.target.value)}
                      disabled={!hatirlatmaVar}
                      className={INPUT_CLS + ' disabled:opacity-50'}
                      aria-label={tr('Hatırlatma tarihi', 'Reminder date')}
                    />
                    <input
                      type="time"
                      value={hatirlatmaTime}
                      onChange={(e) => setHatirlatmaTime(e.target.value)}
                      disabled={!hatirlatmaVar}
                      className={INPUT_CLS + ' disabled:opacity-50 max-w-[120px]'}
                      aria-label={tr('Hatırlatma saati', 'Reminder time')}
                    />
                  </div>
                </div>
              </div>

              {saveError && (
                <p
                  className="text-xs font-bold rounded-lg px-3 py-2"
                  style={{ color: 'var(--crm-danger)', backgroundColor: 'rgba(160,61,44,0.06)', border: '1px solid rgba(160,61,44,0.25)' }}
                >
                  {saveError}
                </p>
              )}

              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-2 border-[var(--brand-primary-shadow)] hover:opacity-90 disabled:opacity-50"
              >
                {saving ? tr('Kaydediliyor...', 'Saving...') : tr('Kaydet', 'Save')}
              </button>
            </div>

            {/* Alt linkler */}
            <div className="flex items-center gap-4">
              <a
                href={`/admin/cockpit?tab=data-gecmis&lead_id=${encodeURIComponent(lead?.lead_id || '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-bold text-[var(--brand-primary)] hover:underline"
              >
                {tr('Data Geçmişi', 'Lead History')}
              </a>
              {/* "Gönderilen SMS" linki burada YOK — message_log koleksiyonunda
                  lead_id alanı yok, besleyecek veri yok. Alan eklendiğinde:
                  <a href={`/admin/messages?lead_id=${lead.lead_id}`}>...</a> */}
            </div>

            {/* Önceki Görüşmeler — varsayılan kapalı (bkz. yukarıdaki yorum) */}
            <div className="rounded-xl border-2 border-[var(--border-light)] overflow-hidden">
              <button
                type="button"
                onClick={toggleGecmis}
                aria-expanded={gecmisAcik}
                aria-controls="lead-detay-gecmis-panel"
                className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm font-bold hover:bg-gray-50"
              >
                <span className="flex items-center gap-2">
                  <History size={14} className="text-[var(--text-muted)]" />
                  {tr('Önceki Görüşmeler', 'Previous Calls')}
                  {gecmisFetched && (
                    <span className="text-xs font-semibold text-[var(--text-muted)]">({gecmisTotal})</span>
                  )}
                </span>
                <ChevronDown
                  size={16}
                  className={`text-[var(--text-muted)] transition-transform ${gecmisAcik ? 'rotate-180' : ''}`}
                />
              </button>
              {gecmisAcik && (
                <div
                  id="lead-detay-gecmis-panel"
                  className="border-t-2 border-[var(--border-light)] divide-y divide-[var(--border-light)] max-h-72 overflow-y-auto"
                >
                  {gecmisLoading && (
                    <div className="p-4 space-y-2">
                      {[1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                    </div>
                  )}
                  {!gecmisLoading && gecmisError && (
                    <div className="p-3"><ErrorRetryCrm onRetry={fetchGecmis} tr={tr} /></div>
                  )}
                  {!gecmisLoading && !gecmisError && gecmisList.length === 0 && (
                    <p className="p-4 text-sm text-center text-[var(--text-muted)]">
                      {tr('Henüz görüşme yok', 'No calls yet')}
                    </p>
                  )}
                  {!gecmisLoading && !gecmisError && gecmisList.map((g) => {
                    const isEditing = editingId === g.gorusme_id;
                    const isHistoryOpen = historyOpenId === g.gorusme_id;
                    const history = historyById[g.gorusme_id] || [];
                    const historyErr = historyErrorById[g.gorusme_id];
                    return (
                      <div key={g.gorusme_id} className="p-3">
                        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-bold">{g.personel_adi || '-'}</span>
                            <StatusBadge status={g.sonuc} options={statusOptions} />
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-[10px] text-[var(--text-muted)]">
                              {fmtDateTime(g.tarih || g.created_at)}
                            </span>
                            {canEditGorusme(g) && !isEditing && (
                              <button
                                type="button"
                                onClick={() => startEditGorusme(g)}
                                className="p-1 rounded hover:bg-gray-100 text-[var(--text-muted)]"
                                aria-label={tr('Notu düzenle', 'Edit note')}
                              >
                                <Pencil size={12} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => toggleHistory(g)}
                              aria-expanded={isHistoryOpen}
                              aria-label={tr('Değişiklik geçmişi', 'Change history')}
                              className="p-1 rounded hover:bg-gray-100 text-[var(--text-muted)]"
                            >
                              <History size={12} />
                            </button>
                          </div>
                        </div>

                        {isEditing ? (
                          <div className="mt-1.5">
                            <textarea
                              value={editNot}
                              onChange={(e) => setEditNot(e.target.value)}
                              rows={2}
                              className={INPUT_CLS + ' resize-none text-xs'}
                              placeholder={tr('Notunuzu girin...', 'Enter note...')}
                            />
                            {editError && (
                              <p className="text-xs font-bold mt-1" style={{ color: 'var(--crm-danger)' }}>
                                {editError}
                              </p>
                            )}
                            <div className="flex justify-end gap-2 mt-1.5">
                              <button
                                type="button"
                                onClick={cancelEditGorusme}
                                disabled={editSaving}
                                className="px-3 py-1 rounded-lg text-xs font-bold text-[var(--text-muted)] hover:bg-gray-100"
                              >
                                {tr('Vazgeç', 'Cancel')}
                              </button>
                              <button
                                type="button"
                                onClick={() => saveEditGorusme(g)}
                                disabled={editSaving || !editNot.trim()}
                                className="px-3 py-1 rounded-lg text-xs font-bold text-white bg-[var(--brand-primary)] disabled:opacity-50"
                              >
                                {editSaving ? tr('Kaydediliyor...', 'Saving...') : tr('Kaydet', 'Save')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          g.not && <p className="text-xs text-[var(--text-muted)] mt-1">{g.not}</p>
                        )}

                        {g.duzenlendi && !isEditing && (
                          <span className="text-[10px] italic text-[var(--text-muted)] mt-1 inline-block">
                            {tr('(düzenlendi)', '(edited)')}
                          </span>
                        )}

                        {isHistoryOpen && (
                          <div className="mt-2 pl-2 border-l-2 border-[var(--border-light)] space-y-1.5">
                            {historyLoadingId === g.gorusme_id && (
                              <p className="text-[10px] text-[var(--text-muted)]">{tr('Yükleniyor...', 'Loading...')}</p>
                            )}
                            {historyLoadingId !== g.gorusme_id && historyErr && (
                              <p className="text-[10px] font-bold" style={{ color: 'var(--crm-danger)' }}>{historyErr}</p>
                            )}
                            {historyLoadingId !== g.gorusme_id && !historyErr && history.length === 0 && (
                              <p className="text-[10px] text-[var(--text-muted)]">
                                {tr('Değişiklik kaydı yok', 'No changes recorded')}
                              </p>
                            )}
                            {history.map((h) => (
                              <div key={h.degisiklik_id} className="text-[10px]">
                                <span className="font-bold">{h.degistiren_adi || '-'}</span>
                                <span className="text-[var(--text-muted)]">
                                  {' '}· {fmtDateTime(h.degisiklik_zamani)} · {h.alan}
                                </span>
                                <p>
                                  <span className="line-through text-[var(--text-muted)] opacity-70">
                                    {h.eski_deger || '-'}
                                  </span>
                                  {' → '}
                                  <span>{h.yeni_deger || '-'}</span>
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* İşlemler — Faz 6 (Atanan Leadler kaldırma turu): Dönüştür /
                Satışa Hazır İşaretle / Kimlik Oluştur / Profil eylemleri.
                Yetki/görünürlük AYNEN eski AdminAssignedLeads.jsx satır içi
                butonlarıyla birebir aynı.
                FAZ 1 düzeltme turu — madde G (2026-09-25): "Kimlik Oluştur"
                linki `lead.lead_id`'ye KORUMASIZ erişiyordu — bu blok
                `detail &&` ile sarılı ama `detail` reset (yukarıdaki
                `useEffect(() => { setDetail(null); ...`) render'dan SONRA
                çalışır; modal kapanırken (`lead` -> null, "Cannot read
                properties of null (reading 'lead_id')" — 2026-08-13'te
                "Data Geçmişi" linkinde AYNI sınıf bulunup düzeltilmişti, bu
                FAZ 1'de eklenen yeni bir satırla YENİDEN açılmıştı) tam
                olarak bu satırın olduğu render'da patlıyordu. `lead?.lead_id`
                ile düzeltildi — bkz. __tests__/admin/
                DanismanCockpitLeadDetayModal.test.js. */}
            <div className="rounded-xl border-2 border-[var(--border-light)] p-3">
              <p className={LBL_CLS + ' mb-2'}>{tr('İşlemler', 'Actions')}</p>
              <div className="flex flex-wrap items-center gap-2">
                {detail.donusturulen_user_id ? (
                  <a
                    href={`/admin/students/${detail.donusturulen_user_id}`}
                    title={tr('Öğrenci Profiline Git', 'Go to Student Profile')}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-lg border border-cyan-200"
                  >
                    <ExternalLink size={13} />
                    {tr('Profil', 'Profile')}
                  </a>
                ) : isAdmin ? (
                  <>
                    <a
                      href={`/admin/students/new-identity?prefill_lead=${lead?.lead_id || ''}&ad_soyad=${encodeURIComponent(detail.ad_soyad || '')}&telefon=${encodeURIComponent(detail.telefon || '')}&email=${encodeURIComponent(detail.email || '')}`}
                      title={tr('Kimlik Oluştur', 'Create Identity')}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200"
                    >
                      <ExternalLink size={13} />
                      {tr('Kimlik Oluştur', 'Create Identity')}
                    </a>
                    <button
                      type="button"
                      onClick={openConvert}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-bold border-2 border-green-600 hover:bg-green-600"
                    >
                      <UserCheck size={13} /> {tr('Dönüştür', 'Convert')}
                    </button>
                  </>
                ) : pendingSalesReq ? (
                  <a
                    href="/admin/cockpit#satis-taleplerim-panel"
                    target="_blank"
                    rel="noopener noreferrer"
                    title={tr('Talebi Görüntüle', 'View request')}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-xs font-bold border-2 border-amber-200"
                  >
                    <Clock size={13} /> {tr('Talep Gönderildi', 'Request Sent')}
                  </a>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={openSatisModal}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-bold border-2 border-green-600 hover:bg-green-600 w-fit ${
                        detail.status === 'kayit_asamasi_ayni_gun' ? 'ring-2 ring-offset-2 ring-green-400' : ''
                      }`}
                    >
                      <Send size={13} /> {tr('Profil Oluştur ve Onaya Gönder', 'Create Profile & Send for Approval')}
                    </button>
                    <p className="text-[10px] text-[var(--text-muted)] max-w-xs">
                      {tr(
                        'Profil oluşturulur ve yönetici onayına gönderilir. Onaydan sonra dersleri ekleyip satışı yapabilirsiniz.',
                        'A profile is created and sent for manager approval. After approval you can add courses and complete the sale.'
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Öğrenciye Dönüştür — alt modal (Radix Dialog DEĞİL, bkz. yukarıdaki
            focus-trap/Escape yorumu). Yalnızca admin/superadmin açabilir. */}
        {convOpen && (
          <div
            className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
            onClick={() => !converting && setConvOpen(false)}
          >
            <div
              ref={convOverlayRef}
              role="dialog"
              aria-modal="true"
              aria-label={tr('Öğrenciye Dönüştür', 'Convert to Student')}
              className="bg-white rounded-2xl w-full max-w-lg p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-bold text-lg mb-1">{tr('Öğrenciye Dönüştür', 'Convert to Student')}</h3>
              <p className="text-xs text-[var(--text-muted)] mb-3">{detail?.ad_soyad || lead?.ad_soyad}</p>
              <div className="grid grid-cols-12 gap-2 items-end mb-2">
                <div className="col-span-5">
                  <label className={LBL_CLS}>{tr('Eğitim', 'Course')}</label>
                  <select value={cCourse} onChange={(e) => handleConvCourseSelect(e.target.value)} className={INPUT_CLS}>
                    <option value="">{tr('Seçiniz', 'Select')}</option>
                    {courses.map((c) => <option key={c.course_id} value={c.course_id}>{c.title}</option>)}
                  </select>
                </div>
                <div className="col-span-3">
                  <label className={LBL_CLS}>{tr('Tür', 'Type')}</label>
                  <select value={cTur} onChange={(e) => setCTur(e.target.value)} className={INPUT_CLS}>
                    <option value="ana_egitim">{tr('Ana Eğitim', 'Main')}</option>
                    <option value="ek_egitim">{tr('Ek Eğitim', 'Additional')}</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className={LBL_CLS}>{tr('Fiyat', 'Price')}</label>
                  <input type="number" value={cFiyat} onChange={(e) => setCFiyat(e.target.value)} className={INPUT_CLS} />
                </div>
                <div className="col-span-2">
                  <button onClick={addConvRow} disabled={!cCourse} className="w-full px-2 py-2 rounded-xl bg-green-500 text-white text-sm font-bold border-2 border-green-600 hover:bg-green-600 disabled:opacity-50">{tr('Ekle', 'Add')}</button>
                </div>
              </div>
              <div className="mb-3 max-h-40 overflow-y-auto">
                {convRows.length === 0 && <p className="text-xs text-[var(--text-muted)] py-2">{tr('Henüz eğitim eklenmedi (eğitimsiz de dönüştürebilirsiniz).', 'No courses yet (you can convert without courses).')}</p>}
                {convRows.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 py-1.5 border-b border-[var(--border-light)] text-sm">
                    <span className="flex-1 font-semibold">{r.title}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.tur === 'ana_egitim' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>{r.tur === 'ana_egitim' ? tr('Ana', 'Main') : tr('Ek', 'Add')}</span>
                    <span className="text-xs font-bold">₺{Number(r.liste_fiyati || 0).toFixed(0)}</span>
                    <button onClick={() => setConvRows((p) => p.filter((_, idx) => idx !== i))} className="p-1 text-red-500 hover:bg-red-50 rounded" aria-label={tr('Kaldır', 'Remove')}><X size={14} /></button>
                  </div>
                ))}
              </div>
              <div className="border-t border-[var(--border-light)] pt-3 mb-3">
                <label className={LBL_CLS}>{tr('Ödeme Şekli', 'Payment')}</label>
                <div className="flex gap-2 mb-2">
                  {[['paid', tr('Ücretli', 'Paid')], ['free', tr('Ücretsiz', 'Free')]].map(([v, lbl]) => (
                    <button key={v} onClick={() => setPayMethod(v)} className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 ${payMethod === v ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]' : 'bg-white border-[var(--border-light)] text-[var(--text-main)]'}`}>{lbl}</button>
                  ))}
                </div>
                {payMethod === 'paid' && (
                  <div className="space-y-2.5">
                    <div>
                      <label className={LBL_CLS}>{tr('İndirim %', 'Discount %')}</label>
                      <input type="number" value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value)} className={INPUT_CLS} />
                    </div>
                    {/* FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan,
                        bkz. components/sales/PaymentPlanFields.jsx dosya başı
                        yorumu. Admin-only modal — role her zaman "admin". */}
                    <PaymentPlanFields
                      key={paymentPlanResetKey}
                      role="admin"
                      totalAmount={convDiscountedTotal}
                      banks={collectionBanks}
                      banksLoading={banksLoading}
                      disabled={converting}
                      onChange={(fields, meta) => { setPaymentPlanFields(fields); setPaymentPlanValid(meta.isValid); }}
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setConvOpen(false)} disabled={converting} className="px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-100">{tr('İptal', 'Cancel')}</button>
                <button onClick={doConvert} disabled={convertDisabled} className="px-5 py-2 rounded-lg bg-green-600 text-white text-sm font-bold hover:bg-green-700 disabled:opacity-50 flex items-center gap-1">
                  <UserCheck size={15} /> {converting ? '...' : tr('Dönüştür', 'Convert')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Profil Oluştur ve Onaya Gönder (eski "Satışa Hazır İşaretle") — alt
            modal (Radix Dialog DEĞİL, bkz. yukarıdaki focus-trap/Escape
            yorumu). Yalnızca danışman açabilir. Endpoint/payload/doğrulama
            AYNEN korunur — bu tur SADECE görünen metni değiştirir (bkz.
            proje-yoneticisi görev talimatı, 2026-08-14). */}
        {satisOpen && (
          <div
            className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
            onClick={() => !satisSaving && setSatisOpen(false)}
          >
            <div
              ref={satisOverlayRef}
              role="dialog"
              aria-modal="true"
              aria-label={tr('Profil Oluştur ve Onaya Gönder', 'Create Profile & Send for Approval')}
              className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-bold text-lg mb-1">{tr('Profil Oluştur ve Onaya Gönder', 'Create Profile & Send for Approval')}</h3>
              <p className="text-xs text-[var(--text-muted)] mb-4">{detail?.ad_soyad || lead?.ad_soyad}</p>
              <div className="space-y-5">
                {/* On kayit kisisel bilgileri — 25 Agustos 2026 (Recep karari).
                    Sihirbazin 1. adimindaki alanlarin aynisi. Onayda
                    _convert_lead_core bunlari ogrenci profiline yazar.
                    Zorunlu DEGIL: danisman elindekini girer, eksigi admin
                    onay ekraninda tamamlar. */}
                <div>
                  <label className={LBL_CLS}>
                    {tr('Kişisel Bilgiler', 'Personal Information')}{' '}
                    <span className="font-normal normal-case text-[var(--text-muted)]">
                      ({tr('ön kayıt', 'pre-registration')})
                    </span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      ['first_name', tr('Ad', 'First name'), 'text'],
                      ['last_name', tr('Soyad', 'Last name'), 'text'],
                      ['email', tr('E-posta', 'Email'), 'email'],
                      ['phone', tr('Telefon', 'Phone'), 'tel'],
                      ['tc_no', tr('TC Kimlik No', 'National ID'), 'text'],
                      ['birth_date', tr('Doğum Tarihi', 'Birth date'), 'date'],
                      ['city', tr('İl', 'City'), 'text'],
                      ['district', tr('İlçe', 'District'), 'text'],
                      ['postal_code', tr('Posta Kodu', 'Postal code'), 'text'],
                      ['address', tr('Adres', 'Address'), 'text'],
                      ['emergency_contact', tr('Acil Durum Kişisi', 'Emergency contact'), 'text'],
                      ['emergency_phone', tr('Acil Durum Telefonu', 'Emergency phone'), 'tel'],
                    ].map(([anahtar, etiket, tip]) => (
                      <div key={anahtar}>
                        <label htmlFor={`onkayit-${anahtar}`} className="block text-[11px] font-bold text-[var(--text-muted)] mb-1">
                          {etiket}
                        </label>
                        <input
                          id={`onkayit-${anahtar}`}
                          type={tip}
                          value={satisKisisel[anahtar] || ''}
                          onChange={(e) => setKisiselAlan(anahtar, e.target.value)}
                          className={INPUT_CLS}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="pt-4 border-t-2 border-[var(--border-light)] space-y-4">
                  <p className="text-[11px] font-bold text-[var(--text-muted)] uppercase tracking-wide">
                    {tr('Satış Bilgileri', 'Sale Information')}
                  </p>
                  <div>
                    <label className={LBL_CLS}>
                      {tr('Önerilen Eğitim(ler)', 'Suggested Course(s)')}{' '}
                      <span className="font-normal normal-case text-[var(--text-muted)]">({tr('opsiyonel', 'optional')})</span>
                    </label>
                    <div className="max-h-32 overflow-y-auto border-2 border-[var(--border-light)] rounded-xl p-2 space-y-1">
                      {courses.map((c) => (
                        <label key={c.course_id} className="flex items-center gap-2 text-xs py-1 cursor-pointer">
                          <input type="checkbox" checked={satisCourseIds.includes(c.course_id)} onChange={() => toggleSatisCourse(c.course_id)} />
                          {c.title}
                        </label>
                      ))}
                      {courses.length === 0 && <p className="text-xs text-[var(--text-muted)]">{tr('Kurs listesi yüklenemedi', 'Courses unavailable')}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={LBL_CLS}>
                        {tr('Önerilen Fiyat', 'Suggested Price')}{' '}
                        <span className="font-normal normal-case text-[var(--text-muted)]">({tr('opsiyonel', 'optional')})</span>
                      </label>
                      <input type="number" value={satisFiyat} onChange={(e) => setSatisFiyat(e.target.value)} className={INPUT_CLS} placeholder="₺" />
                    </div>
                    <div>
                      <label className={LBL_CLS}>{tr('Ödeme Tercihi', 'Payment Preference')}</label>
                      <div className="flex gap-2">
                        {[['pesin', tr('Peşin', 'Cash')], ['taksit', tr('Taksit', 'Installment')], ['bilinmiyor', tr('Bilinmiyor', 'Unknown')]].map(([v, lbl]) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setSatisOdeme(v)}
                            className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-bold border-2 ${satisOdeme === v ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]' : 'bg-white border-[var(--border-light)] text-[var(--text-main)]'}`}
                          >
                            {lbl}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className={LBL_CLS}>{tr('Not', 'Note')} <span className="text-rose-600">*</span></label>
                    <textarea
                      value={satisNot}
                      onChange={(e) => setSatisNot(e.target.value)}
                      rows={3}
                      className={`${INPUT_CLS} resize-none`}
                      placeholder={tr('Danışman notu (en az 10 karakter)...', 'Consultant note (min 10 chars)...')}
                    />
                    {satisNot.length > 0 && !satisNotValid && (
                      <p className="text-[10px] font-bold text-rose-600 mt-1">
                        {tr('Not alanı zorunlu (en az 10 karakter)', 'Note is required (min 10 characters)')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setSatisOpen(false)} disabled={satisSaving} className="px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-100">
                  {tr('İptal', 'Cancel')}
                </button>
                <button
                  onClick={submitSatisTalebi}
                  disabled={satisSaving || !satisNotValid}
                  className="px-5 py-2 rounded-lg bg-green-600 text-white text-sm font-bold hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                >
                  <Send size={15} /> {satisSaving ? '...' : tr("Admin'e Gönder", 'Send to Admin')}
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Telefon hücresi (tel: + WhatsApp) ───────────────────────────────────────

export function TelefonHucre({ telefon, tr }) {
  const waNumber = normalizeTrPhoneForWhatsApp(telefon);
  return (
    <div className="flex items-center gap-2">
      <a
        href={`tel:${telefon}`}
        className="flex items-center gap-1 text-xs hover:underline"
      >
        <Phone size={11} className="text-[var(--text-muted)]" />
        {telefon || '-'}
      </a>
      {waNumber && (
        <a
          href={`https://wa.me/${waNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={tr('WhatsApp ile aç', 'Open in WhatsApp')}
          className="text-green-600 hover:text-green-700"
        >
          <MessageCircle size={13} />
        </a>
      )}
    </div>
  );
}
